/**
 * CollectionSettings - Settings modal for collection configuration
 *
 * Atomic level: Organism
 * Composes: Dialog, Switch, Label
 *
 * Provides settings to enable/disable Regulatory Flags on the collection.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../ui/dialog';
import { Switch } from '../../../ui/switch';
import { Label } from '../../../ui/label';
import { Button } from '../../../ui/button';
import { Settings } from 'lucide-react';

interface CollectionSettingsProps {
  complaintDetectionEnabled: boolean;
  vulnerabilityDetectionEnabled: boolean;
  onComplaintDetectionChange: (enabled: boolean) => void;
  onVulnerabilityDetectionChange: (enabled: boolean) => void;
  className?: string;
}

const CollectionSettings: React.FC<CollectionSettingsProps> = ({
  complaintDetectionEnabled,
  vulnerabilityDetectionEnabled,
  onComplaintDetectionChange,
  onVulnerabilityDetectionChange,
  className,
}) => {
  const [open, setOpen] = React.useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className={className} title="Collection settings">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Collection settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Complaint Detection Setting */}
          <div className="flex items-center justify-between rounded-2xl border border-border/70 bg-background/80 px-4 py-3">
            <Label htmlFor="complaint-detection" className="text-sm font-medium">
              Complaint Detection
            </Label>
            <Switch
              id="complaint-detection"
              checked={complaintDetectionEnabled}
              onCheckedChange={onComplaintDetectionChange}
            />
          </div>

          {/* Vulnerability Detection Setting */}
          <div className="flex items-center justify-between rounded-2xl border border-border/70 bg-background/80 px-4 py-3">
            <Label htmlFor="vulnerability-detection" className="text-sm font-medium">
              Vulnerability Detection
            </Label>
            <Switch
              id="vulnerability-detection"
              checked={vulnerabilityDetectionEnabled}
              onCheckedChange={onVulnerabilityDetectionChange}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { CollectionSettings };
